import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import dayjs from '@/lib/dayjs'
import { cn } from '@/lib/utils'
import { parseSafeDate } from '@/utils/dateUtils'
import type { ReactNode } from 'react'

/** Resolved once: the zone never changes while the tab is open. */
export const BROWSER_TIMEZONE = typeof window !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC'

export const TOOLTIP_DATE_FORMAT = 'DD MMM YY HH:mm:ss'

/** The underline appears under the cursor, so a dense table stays readable until the reader goes looking. */
const TRIGGER_CLASS =
	'min-w-0 cursor-default decoration-dotted decoration-muted-foreground underline-offset-4 hover:underline'

export function TimestampRow({ label, value }: { label: string; value: string }) {
	return (
		<div className='flex items-baseline gap-6'>
			<span className='text-muted-foreground'>{label}</span>
			<span className='ml-auto tabular-nums'>{value}</span>
		</div>
	)
}

export function TimestampRows({ at }: { at: dayjs.Dayjs }) {
	return (
		<>
			<TimestampRow label={BROWSER_TIMEZONE} value={at.tz(BROWSER_TIMEZONE).format(TOOLTIP_DATE_FORMAT)} />
			<TimestampRow label='Relative' value={at.fromNow()} />
		</>
	)
}

/** Head row of a status tooltip: the status mark, what it is about, and the status on the right. */
export function StatusTooltipHeader({ mark, title, status }: { mark: ReactNode; title: string; status?: string }) {
	return (
		<div className='flex items-center gap-2.5 border-b border-border px-3 py-2'>
			{mark}
			<span className='min-w-0 font-medium break-words tabular-nums'>{title}</span>
			{status ? <span className='ml-auto pl-4 text-muted-foreground'>{status}</span> : null}
		</div>
	)
}

interface TimestampTooltipProps {
	/** The moment behind the value. Anything unparseable renders the children on their own. */
	value: string | number | Date | null | undefined
	children: ReactNode
	/** Heading for the tooltip. Pass the value itself where the trigger can be truncated, omit it
	 *  where the trigger already reads as a timestamp. */
	title?: ReactNode
	dataTestId?: string
	/** Applied to the trigger, which replaces the child's own box in the layout. */
	className?: string
	/** Adds the UTC reading. Opt in where the reader works across zones, for example raw stream data. */
	showUtc?: boolean
	/** Adds the epoch value, for copying into a query or a log search. */
	showEpoch?: boolean
}

/**
 * Wraps a value so its timestamp is one hover away, rather than taking a line of its own under every
 * row. The trigger stays the value the user came to read.
 */
export function TimestampTooltip({
	value,
	children,
	title,
	dataTestId,
	className,
	showUtc = false,
	showEpoch = false,
}: TimestampTooltipProps) {
	const parsed = value === null || value === undefined ? null : parseSafeDate(value)
	if (!parsed?.isValid()) {
		return <>{children}</>
	}

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<span className={cn(TRIGGER_CLASS, className)} data-testid={dataTestId}>
					{children}
				</span>
			</TooltipTrigger>
			<TooltipContent side='top' className='max-w-xs p-0'>
				{title ? <div className='border-b border-border px-3 py-2 font-medium break-words'>{title}</div> : null}
				<div className='flex flex-col gap-1 px-3 py-2'>
					{showUtc ? (
						<TimestampRow label='UTC' value={dayjs(parsed.toDate()).utc().format(TOOLTIP_DATE_FORMAT)} />
					) : null}
					<TimestampRows at={dayjs(parsed.toDate())} />
					{showEpoch ? <TimestampRow label='Timestamp' value={String(parsed.valueOf())} /> : null}
				</div>
			</TooltipContent>
		</Tooltip>
	)
}
